"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.loadFile = void 0;
const jszip_1 = __importDefault(require("jszip"));
const path_1 = require("path");
const ajv_1 = __importDefault(require("ajv"));
const ajv_formats_1 = __importDefault(require("ajv-formats"));
const ajv_keywords_1 = __importDefault(require("ajv-keywords"));
const ajv_errors_1 = __importDefault(require("ajv-errors"));
const constants_1 = require("../item/constants");
const util_1 = require("util");
const constants_2 = require("./constants");
const types_1 = require("./types");
const schemas_1 = require("./schemas");
const files_errors_1 = require("./files.errors");
const errorHandler_1 = require("./errorHandler");
const schemas_2 = require("@dcl/schemas");
const ajv = new ajv_1.default({ $data: true, allErrors: true });
(0, ajv_formats_1.default)(ajv);
(0, ajv_keywords_1.default)(ajv);
(0, ajv_errors_1.default)(ajv, { singleError: true });
const validator = ajv
    .addSchema(schemas_1.WearableConfigSchema, 'WearableConfig')
    .addSchema(schemas_1.SceneConfigSchema, 'SceneConfig')
    .addSchema(schemas_1.BuilderConfigSchema, 'BuilderConfig')
    .addSchema(schemas_1.EmoteConfigSchema, 'EmoteConfig');
async function loadFile(fileName, file) {
    const extension = getExtension(fileName);
    if (extension === '.zip') {
        return handleZippedModelFiles(file);
    }
    else if (isModelPath(fileName)) {
        return handleFileModel(fileName, file);
    }
    else {
        throw new files_errors_1.WrongExtensionError(fileName);
    }
}
exports.loadFile = loadFile;
function getExtension(fileName) {
    const matches = fileName.match(/\.[0-9a-z]+$/i);
    const extension = matches ? matches[0] : null;
    return extension;
}
function isImageFile(fileName) {
    return fileName.toLowerCase().endsWith('.png');
}
function isModelFile(fileName) {
    fileName = fileName.toLowerCase();
    return fileName.endsWith('.gltf') || fileName.endsWith('.glb');
}
function isModelPath(fileName) {
    fileName = fileName.toLowerCase();
    // we ignore PNG files that end with "_mask", since those are auxiliary
    const isMask = fileName.includes('_mask');
    return (isModelFile(fileName) ||
        (fileName.indexOf(constants_1.THUMBNAIL_PATH) === -1 &&
            !isMask &&
            isImageFile(fileName)));
}
/**
 * Unzips files and build the content record and asset config.
 * One of the models will be taken into consideration if multiple models are uploaded.
 *
 * @param zipFile - The ZIP file.
 */
async function handleZippedModelFiles(zipFile) {
    const zip = await jszip_1.default.loadAsync(zipFile);
    const fileNames = [];
    const promiseOfFileContents = [];
    let fileFormat;
    if (globalThis.Blob && zipFile instanceof globalThis.Blob) {
        fileFormat = 'blob';
    }
    else if (Buffer.isBuffer(zipFile)) {
        fileFormat = 'nodebuffer';
    }
    else if (zipFile instanceof Uint8Array) {
        fileFormat = 'uint8array';
    }
    else if (zipFile instanceof ArrayBuffer) {
        fileFormat = 'arraybuffer';
    }
    zip.forEach((filePath, file) => {
        if (!(0, path_1.basename)(filePath).startsWith('.') &&
            (0, path_1.basename)(filePath) !== constants_2.WEARABLE_MANIFEST &&
            (0, path_1.basename)(filePath) !== constants_2.BUILDER_MANIFEST &&
            (0, path_1.basename)(filePath) !== constants_2.SCENE_MANIFEST &&
            (0, path_1.basename)(filePath) !== constants_2.EMOTE_MANIFEST) {
            fileNames.push(filePath);
            promiseOfFileContents.push(file.async(fileFormat));
        }
    });
    const fileContents = await Promise.all(promiseOfFileContents);
    const content = fileNames.reduce((acc, fileName, index) => {
        acc[fileName] = fileContents[index];
        return acc;
    }, {});
    let wearable = undefined;
    let scene = undefined;
    let builder = undefined;
    let emote = undefined;
    const wearableZipFile = zip.file(constants_2.WEARABLE_MANIFEST);
    const sceneZipFile = zip.file(constants_2.SCENE_MANIFEST);
    const builderZipFile = zip.file(constants_2.BUILDER_MANIFEST);
    const emoteZipFile = zip.file(constants_2.EMOTE_MANIFEST);
    if (!wearableZipFile && sceneZipFile) {
        throw new files_errors_1.FileNotFoundError(constants_2.WEARABLE_MANIFEST);
    }
    else if (wearableZipFile) {
        const wearableFileContents = await wearableZipFile.async('uint8array');
        wearable = await loadWearableConfig(wearableFileContents);
        const wearableData = wearable.data;
        wearableData.representations.forEach((representation) => {
            representation.contents.forEach((representationContent) => {
                if (!zip.file(representation.mainFile)) {
                    throw new files_errors_1.FileNotFoundError(representationContent);
                }
                const isSkin = wearableData.category == schemas_2.WearableCategory.SKIN;
                let size;
                if (globalThis.Blob &&
                    content[representation.mainFile] instanceof globalThis.Blob) {
                    size = content[representation.mainFile].size;
                }
                else {
                    size = content[representation.mainFile].length;
                }
                if (isSkin && size > constants_2.MAX_SKIN_FILE_SIZE) {
                    throw new files_errors_1.FileTooBigError(representation.mainFile, size, constants_2.MAX_SKIN_FILE_SIZE, types_1.FileType.SKIN);
                }
                if (!isSkin && size > constants_2.MAX_WEARABLE_FILE_SIZE) {
                    throw new files_errors_1.FileTooBigError(representation.mainFile, size, constants_2.MAX_WEARABLE_FILE_SIZE, types_1.FileType.WEARABLE);
                }
            });
        });
        // Verify Max thumbnail size
        Object.keys(content).forEach((file) => {
            if (file.endsWith('.png') && content[file] instanceof Uint8Array) {
                let size;
                if (globalThis.Blob && content[file] instanceof globalThis.Blob) {
                    size = content[file].size;
                }
                else {
                    size = content[file].length;
                }
                if (size > constants_2.MAX_THUMBNAIL_FILE_SIZE) {
                    throw new files_errors_1.FileTooBigError(file, size, constants_2.MAX_THUMBNAIL_FILE_SIZE, types_1.FileType.THUMBNAIL);
                }
            }
        });
        // Smart Wearables also have a scene.json file representing the required permissions
        if (sceneZipFile) {
            const sceneFileContents = await sceneZipFile.async('uint8array');
            scene = await loadSceneConfig(sceneFileContents);
            if (!zip.file(scene.main)) {
                throw new files_errors_1.FileNotFoundError(scene.main);
            }
        }
    }
    if (builderZipFile) {
        const builderZipFileContents = await builderZipFile.async('uint8array');
        builder = await loadBuilderConfig(builderZipFileContents);
    }
    if (emoteZipFile) {
        const emoteZipFileContents = await emoteZipFile.async('uint8array');
        emote = await loadEmoteConfig(emoteZipFileContents);
        Object.keys(content).forEach((file) => {
            if ((file.endsWith('.glb') || file.endsWith('.gltf')) &&
                content[file] instanceof Uint8Array) {
                let size;
                if (globalThis.Blob && content[file] instanceof globalThis.Blob) {
                    size = content[file].size;
                }
                else {
                    size = content[file].length;
                }
                if (size > constants_2.MAX_EMOTE_FILE_SIZE) {
                    throw new files_errors_1.FileTooBigError(file, size, constants_2.MAX_EMOTE_FILE_SIZE, types_1.FileType.EMOTE);
                }
            }
        });
    }
    let result = { content };
    if (builder) {
        result = { ...result, builder };
    }
    if (emote) {
        result = { ...result, emote };
    }
    if (wearable) {
        result = { ...result, wearable, scene };
    }
    else {
        const mainModelFile = fileNames.find(isModelPath);
        if (!mainModelFile) {
            throw new files_errors_1.ModelFileNotFoundError();
        }
        result = { ...result, mainModel: mainModelFile };
    }
    return result;
}
function handleFileModel(fileName, file) {
    return { content: { [fileName]: file }, mainModel: fileName };
}
async function readContent(file) {
    if (globalThis.Blob && file instanceof globalThis.Blob) {
        return file.text();
    }
    else if (globalThis.TextDecoder) {
        return new TextDecoder('utf-8').decode(file);
    }
    return new util_1.TextDecoder('utf-8').decode(file);
}
async function loadBuilderConfig(file) {
    const content = await readContent(file);
    const parsedContent = JSON.parse(content);
    if (!validator.validate('BuilderConfig', parsedContent)) {
        throw new files_errors_1.InvalidBuilderConfigFileError(validator.errors);
    }
    return parsedContent;
}
async function loadWearableConfig(file) {
    const content = await readContent(file);
    const parsedContent = JSON.parse(content);
    if (!validator.validate('WearableConfig', parsedContent)) {
        throw new files_errors_1.InvalidWearableConfigFileError(validator.errors);
    }
    return parsedContent;
}
async function loadSceneConfig(file) {
    const content = await readContent(file);
    const parsedContent = JSON.parse(content);
    if (!validator.validate('SceneConfig', parsedContent)) {
        (0, errorHandler_1.handleAjvErrors)(parsedContent, validator.errors);
    }
    return parsedContent;
}
async function loadEmoteConfig(file) {
    const content = await readContent(file);
    const validProperties = [
        'name',
        'description',
        'tags',
        'rarity',
        'category',
        'play_mode'
    ];
    const parsedContent = JSON.parse(content);
    validator.validate('EmoteConfig', parsedContent);
    const values = Object.keys(parsedContent).reduce((val, key) => {
        var _a;
        const propertyHasErrors = (_a = validator.errors) === null || _a === void 0 ? void 0 : _a.some(({ instancePath }) => instancePath.includes(key));
        if (propertyHasErrors || !validProperties.includes(key)) {
            return val;
        }
        return {
            ...val,
            [key]: parsedContent[key]
        };
    }, {});
    return values;
}
//# sourceMappingURL=files.js.map